/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding logMaint attributes.
 *
 * @author urahish
 * 02/27/2023
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import '../view-audit/viewAuditFilterForm.css'
import API from "../../../../../utils/api";
import Loader from "../../../loader/loader";

/**
 * HOC for the logMaint form
 */
class LogMaintForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            logType: '',
            logTypeName: '',
            logTypeFromDate: '',
            logTypeToDate: '',

            isDisplayLoader: true,
            errors: {},

            // form fields
            fields: [
                {
                    inputType: 'dropdown',
                    labelName: 'Log Type',
                    inputName: 'logType',
                    optionValueName: 'logType',
                    optionLabelName: 'logTypeName',
                    required: true,
                    path: '/logType',
                    valueType: 'logType'
                },
                {
                    inputType: 'dateOnlyPicker',
                    labelName: 'From',
                    inputName: 'logTypeFromDate',
                    optionValueName: 'logTypeFromDate',
                    optionLabelName: 'logTypeFromDate',
                    valueType: 'startDate'
                },
                {
                    inputType: 'dateOnlyPicker',
                    labelName: 'To',
                    inputName: 'logTypeToDate',
                    optionValueName: 'logTypeToDate',
                    optionLabelName: 'logTypeToDate',
                    valueType: 'endDate'
                }
            ]
        };

        EventEmitter.emit('setPageHeaderData', {
            page: 'logMaint',
            pageName: "Log Maint",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/logMaint",
            optionalIcons: ['manageColumnsIcon']
        });

        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteLogs);

    }

    componentDidMount = async () => {
        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);
        oldState.token = token;
        oldState.isDisplayLoader = false;

        this.setState(oldState);
    };

    componentWillUnmount = () => {
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteLogs);
    };

    /**
     * update the state when user inout a value
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    handleSubmit = () => {
        // data to be sent
        let data = {
            logType: this.state.logType,
            logTypeFromDate: this.state.logTypeFromDate,
            logTypeToDate: this.state.logTypeToDate
        };

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            const newState = Object.assign({}, this.state);
            newState.isDisplayLoader = true;
            this.setState(newState);

            this.displayDeleteWarning(data);
        }
    };

    validate = (key, value) => {
        let error = {};
        const formattedDate = this.getTheDateBeforeThreeMonth().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

        switch (key) {
            case 'logType':
                if (value === '')
                    error[key] = "Please select a Log type!";
                else
                    error[key] = "";
                break;
            case 'logTypeFromDate':
                if (value === '')
                    error[key] = "Please select a start date!";
                else if (this.isFromDateWithinThreeMonths())
                    error[key] = "Start Date must not be a Date within last 3 months! Please select a Date on or before " + formattedDate;
                else if (this.validateFromDate(value))
                    error[key] = "Start Date must be less than or equal to the End date!";
                else
                    error[key] = "";
                break;
            case 'logTypeToDate':
                if (value === '')
                    error[key] = "Please select a End date!";
                else if (this.isToDateWithinThreeMonths())
                    error[key] = "End Date must not be a Date within last 3 months! Please select a Date on or before " + formattedDate;
                else if (this.validateToDate(value))
                    error[key] = "End Date must be greater than or equal to the Start date!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    validateFromDate = (value) => {
        const newState = Object.assign({}, this.state);

        const fromDate = new Date(value);
        const toDate = new Date(newState.logTypeToDate);

        return fromDate > toDate;
    };

    validateToDate = (value) => {
        const newState = Object.assign({}, this.state);

        const fromDate = new Date(newState.logTypeFromDate);
        const toDate = new Date(value);

        return fromDate > toDate;
    };

    isFromDateWithinThreeMonths = () => {
        const newState = Object.assign({}, this.state);

        const fromDate = new Date(newState.logTypeFromDate);
        fromDate.setHours(0, 0, 0, 0);

        const threeMonthsAgoDate = this.getTheDateBeforeThreeMonth();

        return fromDate > threeMonthsAgoDate;
    };

    isToDateWithinThreeMonths = () => {
        const newState = Object.assign({}, this.state);

        const toDate = new Date(newState.logTypeToDate);
        toDate.setHours(0, 0, 0, 0);

        const threeMonthsAgoDate = this.getTheDateBeforeThreeMonth();

        return toDate > threeMonthsAgoDate;
    };

    getTheDateBeforeThreeMonth = () => {
        const currentDate = new Date();
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(currentDate.getMonth() - 3);
        threeMonthsAgo.setHours(0, 0, 0, 0);
        return threeMonthsAgo;
    };

    displayDeleteWarning = async (data) => {
        await API.post('/logMaint',
            {
                "logType": data.logType,
                "startDate": data.logTypeFromDate,
                "endDate": data.logTypeToDate
            }, {headers: {Authorization: this.state.token}}
        ).then(res => {
            const newState = Object.assign({}, this.state);
            newState.isDisplayLoader = false;
            this.setState(newState);
            this.displayDeleteWarningMessage(res)
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    displayDeleteWarningMessage = (data) => {
        if (data.data !== 0) {
            EventEmitter.emit('warningModelData', {
                type: 'logMaint',
                name: data.data + " records"
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "There are no any records to delete for the given time period!",
                alertTitle: "Error"
            });
        }
    };

    deleteLogs = async (status) => {
        if (status) {
            const data = Object.assign({}, this.state);
            data.isDisplayLoader = true;
            this.setState(data);

            await API.post('/logMaint?cf_Submission=true',
                {
                    "logType": data.logType,
                    "startDate": data.logTypeFromDate,
                    "endDate": data.logTypeToDate
                }, {headers: {Authorization: this.state.token}}
            ).then(res => {
                this.clearForm();
                this.setAlert("Success", "Success", "Successfully Deleted the records");
            }).catch(er => {
                this.displayErrorsForAPICalls(er);
            });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "There are no any records to delete for the given time period!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    clearForm = () => {
        const newState = Object.assign({}, this.state);
        newState.logType = '';
        newState.logTypeName = '';
        newState.logTypeFromDate = '';
        newState.logTypeToDate = '';
        newState.isDisplayLoader = false;

        newState.errors = {};

        this.setState(newState);
    };

    /**
     * fire alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    render() {
        return(
            <div className="view-audit-filter-container">
                {this.state.isDisplayLoader && <Loader/>}
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
                <div className="view-audit-filter-button-container">
                    <input type="button" className="view-audit-filter-cancel" value="Clear"
                           onClick={() => this.clearForm()} />
                    <input type="button" className="view-audit-filter-submit" value="Delete Logs"
                           onClick={() => this.handleSubmit()}/>
                </div>
            </div>
        )
    }
}

export default LogMaintForm;
